import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useQuery } from 'react-apollo';

import { getAsset } from '../../../../../../../../util/assets';
import { PrintProductConfiguratorModal } from '../../../../../../../../services/prints/components/PrintProductConfigurator';
import { getPreferredPrintProductSizeOrientation } from '../../PurchasingOptions/components/PrintProductsGroup/helpers';
import PrintProductSizeRoesConfiguratorModal from '../../PurchasingOptions/components/PrintProductsGroup/components/PrintProductSizeRoesConfigurator';
import Button from '../../../../../../../../components/global/Button';
import CardElementPhotoSelectionControl from '../../../../../../../../services/cards/components/CardElementPhoto/CardElementPhotoSelectionControl/CardElementPhotoSelectionControl';
import { getPhotoPricingPrintBackgrounds } from '../../../../../../../../util/photo-pricings';
import useIdentityJerseyColors from '../../../hooks/useIdentityJerseyColors';
import { printProductSizeBaseInfo } from '../../../../../../../../graphql/queries/print-product';

const propTypes = {
  className: PropTypes.string,
  albumMembershipPhoto: PropTypes.shape({
    photo: PropTypes.shape({
      id: PropTypes.string,
    }),
    album: PropTypes.shape({
      id: PropTypes.string,
    }),
  }),
  albumMembershipGroup: PropTypes.shape({
    id: PropTypes.string.isRequired,
    is_album: PropTypes.bool,
    album: PropTypes.shape({
      root_album_folder: PropTypes.shape({
        id: PropTypes.string,
      }).isRequired,
    }),
  }),
  purchasedPhotoPricing: PropTypes.shape({
    id: PropTypes.string,
    photo_pricing: PropTypes.shape({
      id: PropTypes.string.isRequired,
    }).isRequired,
    photo_pricing_print_product_item: PropTypes.shape({
      print_product_configuration: PropTypes.shape({
        print_product: PropTypes.shape({
          id: PropTypes.string,
        }),
        print_product_size: PropTypes.shape({
          id: PropTypes.string,
        }),
      }),
    }),
  }),
  cartItem: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }),
  identity: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }),
  onUpdate: PropTypes.func.isRequired,
  onConfigurationComplete: PropTypes.func,
  onPurchasedPhotoPricingPrintProductToggle: PropTypes.func.isRequired,
  isAllPhotos: PropTypes.bool,
};

const PhotoPricingPrintProductOption = ({
  className,
  albumMembershipPhoto,
  albumMembershipGroup,
  purchasedPhotoPricing,
  cartItem,
  identity,
  onUpdate,
  onConfigurationComplete,
  onPurchasedPhotoPricingPrintProductToggle,
  isAllPhotos = false,
  watermarkSettings,
}) => {
  const { photo_pricing_print_product_item: photoPricingPrintProductItem } =
    purchasedPhotoPricing;
  const {
    print_product_configuration: {
      name,
      print_product: printProduct,
      print_product_size: printProductSize,
    },
  } = photoPricingPrintProductItem;

  const [configuringPhotoData, setConfiguringPhotoData] = useState(
    albumMembershipPhoto ?? null,
  );
  const [selectingPhoto, setSelectingPhoto] = useState(false);
  const [configuring, setConfiguring] = useState(false);
  const {
    data: { printProducts: printProductWithSizeInfo } = {},
    loading: loadingPrintProductSizeInfo,
  } = useQuery(printProductSizeBaseInfo, {
    variables: {
      print_product_id: printProduct?.id,
      print_product_size_id: printProductSize?.id,
    },
    skip: !printProduct?.id || !printProductSize?.id,
    fetchPolicy: 'cache-first',
  });
  const printProductSizeWithOrientations =
    printProductWithSizeInfo?.records?.[0]?.sizes?.records?.[0] ??
    printProductSize;
  const { photo, album } = configuringPhotoData ?? {};
  const {
    accountCard: currentCard,
    roesConfiguration: currentRoesConfiguration,
  } = cartItem ?? {};
  const { photo_pricing: photoPricing } = purchasedPhotoPricing;
  const photoPricingPrintProducts =
    photoPricing?.print_product_items?.records ?? [];

  const { jerseyColors } = useIdentityJerseyColors({
    identityId: identity?.id,
    albumFolderId:
      albumMembershipGroup?.album_folder?.id ??
      albumMembershipGroup?.album?.root_album_folder?.id,
  });

  const printBackgrounds = photo?.foreground?.original_url
    ? getPhotoPricingPrintBackgrounds(
        photoPricing,
        albumMembershipGroup,
        jerseyColors,
      )
    : [];

  const classes = classNames({
    [className]: !!className,
    'purchases-pending-configuration-option-item': true,
    'purchases-pending-configuration-option-item-print-product': true,
    'is-selected': !!cartItem,
  });

  return (
    <div className={classes}>
      <div className="purchases-pending-configuration-option-item-thumb">
        <img
          src={
            printProduct?.preview_asset_image?.url ??
            getAsset('/icon-custom-print.svg')
          }
          width="62px"
          alt=""
        />
      </div>
      <div className="purchasing-option-item-content-wrap">
        <span className="purchasing-option-item-title">{name}</span>
        <Button
          pill
          btnSize="sm"
          btnOutline={!cartItem}
          className="text-bold"
          disabled={loadingPrintProductSizeInfo}
          onClick={() => {
            if (!cartItem && albumMembershipPhoto) {
              setConfiguring(true);
            } else if (!cartItem) {
              setSelectingPhoto(true);
            }
          }}
        >
          {cartItem ? 'Update configurations' : 'Configure now'}
        </Button>
      </div>
      {configuring && photo && printProduct?.card && (
        <PrintProductConfiguratorModal
          forceOpen
          controlsDisabled
          printProductId={printProduct.id}
          printProductSizeId={printProductSize.id}
          availablePrintProductSizeIds={[printProductSize.id]}
          defaultAccountCard={currentCard}
          photo={photo}
          photoId={photo.id}
          orientation={getPreferredPrintProductSizeOrientation({
            printProductSize: printProductSizeWithOrientations,
            photo,
          })}
          onClose={() => {
            setConfiguring(false);
          }}
          onComplete={({ accountCard, albumCustomPrint }) => {
            if (cartItem) {
              setConfiguring(false);
              return;
            }

            onPurchasedPhotoPricingPrintProductToggle({
              photo,
              album,
              accountCard,
              albumCustomPrint,
            });

            onConfigurationComplete?.();
            setConfiguring(false);
          }}
          albumMembershipGroup={albumMembershipGroup}
          identity={identity}
          printBackgrounds={printBackgrounds}
          watermarkSettings={watermarkSettings}
        />
      )}
      {configuring && photo && !printProduct?.card && (
        <PrintProductSizeRoesConfiguratorModal
          forceOpen
          printProduct={printProduct}
          printProductSize={printProductSize}
          defaultConfiguration={currentRoesConfiguration}
          photo={photo}
          identity={identity}
          albumMembershipGroup={albumMembershipGroup}
          isAllPhotos={isAllPhotos}
          onComplete={({ roesConfiguration }) => {
            if (cartItem) {
              onUpdate(cartItem.id, { roesConfiguration });
              setConfiguring(false);
              return;
            }
            onPurchasedPhotoPricingPrintProductToggle({
              photo,
              album,
              photoPricingPrintProduct: photoPricingPrintProducts.find(
                photoPricingPrintProduct =>
                  photoPricingPrintProduct.print_product.id ===
                    printProduct.id &&
                  photoPricingPrintProduct.print_product_size.id ===
                    printProductSize.id,
              ),
              roesConfiguration,
            });

            onConfigurationComplete?.();
            setConfiguring(false);
          }}
          onClose={() => {
            setConfiguring(false);
          }}
        />
      )}
      <CardElementPhotoSelectionControl
        forceOpen={selectingPhoto}
        onClose={() => setSelectingPhoto(false)}
        onRecordChange={matchedPhoto => {
          if (matchedPhoto?.__typename === 'Photo') {
            setConfiguringPhotoData({
              photo: matchedPhoto,
              album:
                matchedPhoto.albums?.records?.[0] ??
                albumMembershipGroup?.album,
            });
          } else {
            setConfiguringPhotoData(matchedPhoto);
          }
          setConfiguring(true);
        }}
        photosContext={{
          album_id:
            albumMembershipGroup &&
            (albumMembershipGroup.is_album
              ? albumMembershipGroup.id
              : undefined),
          album_folder_id:
            albumMembershipGroup &&
            (!albumMembershipGroup.is_album
              ? albumMembershipGroup.id
              : undefined),
          root_album_folder_id:
            albumMembershipGroup && albumMembershipGroup.is_album
              ? albumMembershipGroup.album.root_album_folder?.id
              : undefined,
          // We rely on a resolver that the membershipGroup would have by
          // default when we don't create a pseudo membership (identities).
          // This allows us to actually discern if this is a membership or not.
          is_album_membership_group: !!albumMembershipGroup?.identities,
          is_all_photos: isAllPhotos,
        }}
        mappings={
          isAllPhotos
            ? undefined
            : [
                {
                  id: 'allPhotos',
                  label: 'All Photos',
                  path: 'all_photos',
                },
                {
                  id: 'matches',
                  label: 'Matches',
                  path: 'matched_photos_extended',
                },
              ]
        }
        uploadDisabled
        watermarkSettings={watermarkSettings}
      />
    </div>
  );
};

PhotoPricingPrintProductOption.propTypes = propTypes;

export default PhotoPricingPrintProductOption;
